/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: sms/pages/SmsSettingsPage.tsx
 🗓️ Created: 2026-01-20
 🧩 Description:
    SMS Settings page - 4-column inline layout with Toggle
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { Settings, Globe, Clock, Gauge } from 'lucide-react';
import { ModuleFormHeader } from '../../../components/layouts';
import { FixedActionBar } from '../../../components/ui/FixedActionBar';
import { FormBox } from '../../../components/ui/FormBox';
import { Input } from '../../../components/ui/input';
import { Toggle } from '../../../components/ui/Toggle';
import { Label } from '../../../components/ui/label';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { Textarea } from '../../../components/ui/textarea';
import { TimezoneSelect } from '../../../components/ui/TimezoneSelect';
import { useNotifications } from '../../../contexts/NotificationContext';
import { SmsSettings } from '../types';
import { fetchSettings, updateSettings } from '../api';

export default function SmsSettingsPage() {
    const { t } = useI18n();
    const { showSuccess, showError } = useNotifications();

    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState<SmsSettings>({
        domainId: 0,
        smsEnabled: true,
        allowedCountries: [],
        blockedPrefixes: [],
        rateLimitPerMinute: 60,
        rateLimitPerHour: 500,
        rateLimitPerDay: 2000,
        quietHoursEnabled: false,
        quietHoursStart: '22:00',
        quietHoursEnd: '08:00',
        quietHoursTimezone: 'America/New_York',
    });
    const [originalFormData, setOriginalFormData] = useState<SmsSettings>(formData);

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        try {
            setIsLoading(true);
            const settings = await fetchSettings();
            setFormData(settings);
            setOriginalFormData(settings);
        } catch (error) {
            showError(error instanceof Error ? error.message : t('sms.settings.messages.loadError'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleFieldChange = useCallback(<K extends keyof SmsSettings>(field: K, value: SmsSettings[K]) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    }, []);

    const parseList = (value: string): string[] => {
        return value.split(',').map(s => s.trim()).filter(Boolean);
    };

    const formatList = (arr: string[]): string => {
        return arr.join(', ');
    };

    const handleSave = useCallback(async () => {
        try {
            setIsSaving(true);
            await updateSettings(formData);
            setOriginalFormData(formData);
            showSuccess(t('sms.settings.messages.saveSuccess'));
            return { success: true };
        } catch (error) {
            showError(error instanceof Error ? error.message : t('sms.settings.messages.saveError'));
            return undefined;
        } finally {
            setIsSaving(false);
        }
    }, [formData, showSuccess, showError, t]);

    const handleSaveWithAction = useCallback(async (action: 'save' | 'save-and-close' | 'save-and-new') => {
        await handleSave();
    }, [handleSave]);

    const handleCancel = useCallback(() => {
        setFormData(originalFormData);
    }, [originalFormData]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-muted-foreground">{t('common.loading')}</div>
            </div>
        );
    }

    return (
        <div className="p-6">
            <ModuleFormHeader
                icon={<Settings className="h-4 w-4 text-primary" />}
                title={t('sms.settings.title')}
                backButtonText={t('common.exit')}
                onBack={() => window.history.back()}
            />

            <div className="mt-8 space-y-4">
                {/* General Settings */}
                <FormBox
                    title={t('sms.settings.general')}
                    icon={<Settings className="h-5 w-5" />}
                >
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                        <SimpleTooltip content={t('sms.settings.smsEnabled')} position="top">
                            <Label htmlFor="smsEnabled" className="text-left">
                                {t('sms.settings.smsEnabled')}
                            </Label>
                        </SimpleTooltip>
                        <Toggle
                            id="smsEnabled"
                            value={formData.smsEnabled}
                            onChange={(value) => handleFieldChange('smsEnabled', value)}
                        />
                        <div /><div />
                    </div>
                </FormBox>

                {/* Country/Prefix Filtering */}
                <FormBox
                    title={t('sms.settings.filtering')}
                    icon={<Globe className="h-5 w-5" />}
                    variant="elevated"
                >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <SimpleTooltip content={t('sms.settings.allowedCountriesHelp')} position="top">
                                <Label htmlFor="allowedCountries">
                                    {t('sms.settings.allowedCountries')}
                                </Label>
                            </SimpleTooltip>
                            <Textarea
                                id="allowedCountries"
                                placeholder="US, CA, MX, GB"
                                value={formatList(formData.allowedCountries || [])}
                                onChange={(e) => handleFieldChange('allowedCountries', parseList(e.target.value))}
                                rows={3}
                            />
                            <p className="text-xs text-muted-foreground">
                                {t('sms.settings.allowedCountriesHelp')}
                            </p>
                        </div>

                        <div className="space-y-2">
                            <SimpleTooltip content={t('sms.settings.blockedPrefixesHelp')} position="top">
                                <Label htmlFor="blockedPrefixes">
                                    {t('sms.settings.blockedPrefixes')}
                                </Label>
                            </SimpleTooltip>
                            <Textarea
                                id="blockedPrefixes"
                                placeholder="+1900, +1809"
                                value={formatList(formData.blockedPrefixes || [])}
                                onChange={(e) => handleFieldChange('blockedPrefixes', parseList(e.target.value))}
                                rows={3}
                            />
                            <p className="text-xs text-muted-foreground">
                                {t('sms.settings.blockedPrefixesHelp')}
                            </p>
                        </div>
                    </div>
                </FormBox>

                {/* Rate Limits */}
                <FormBox
                    title={t('sms.settings.rateLimits')}
                    icon={<Gauge className="h-5 w-5" />}
                >
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                        <SimpleTooltip content={t('sms.settings.perMinute')} position="top">
                            <Label htmlFor="rateLimitPerMinute" className="text-left">
                                {t('sms.settings.perMinute')}
                            </Label>
                        </SimpleTooltip>
                        <Input
                            id="rateLimitPerMinute"
                            type="number"
                            value={formData.rateLimitPerMinute || 60}
                            onChange={(e) => handleFieldChange('rateLimitPerMinute', parseInt(e.target.value) || 60)}
                        />
                        <SimpleTooltip content={t('sms.settings.perHour')} position="top">
                            <Label htmlFor="rateLimitPerHour" className="text-left">
                                {t('sms.settings.perHour')}
                            </Label>
                        </SimpleTooltip>
                        <Input
                            id="rateLimitPerHour"
                            type="number"
                            value={formData.rateLimitPerHour || 500}
                            onChange={(e) => handleFieldChange('rateLimitPerHour', parseInt(e.target.value) || 500)}
                        />

                        <SimpleTooltip content={t('sms.settings.perDay')} position="top">
                            <Label htmlFor="rateLimitPerDay" className="text-left">
                                {t('sms.settings.perDay')}
                            </Label>
                        </SimpleTooltip>
                        <Input
                            id="rateLimitPerDay"
                            type="number"
                            value={formData.rateLimitPerDay || 2000}
                            onChange={(e) => handleFieldChange('rateLimitPerDay', parseInt(e.target.value) || 2000)}
                        />
                        <div /><div />
                    </div>
                </FormBox>

                {/* Quiet Hours */}
                <FormBox
                    title={t('sms.settings.quietHours')}
                    icon={<Clock className="h-5 w-5" />}
                >
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                        <SimpleTooltip content={t('sms.settings.quietHoursEnabled')} position="top">
                            <Label htmlFor="quietHoursEnabled" className="text-left">
                                {t('sms.settings.quietHoursEnabled')}
                            </Label>
                        </SimpleTooltip>
                        <Toggle
                            id="quietHoursEnabled"
                            value={formData.quietHoursEnabled}
                            onChange={(value) => handleFieldChange('quietHoursEnabled', value)}
                        />
                        <div /><div />

                        <SimpleTooltip content={t('sms.settings.startTime')} position="top">
                            <Label htmlFor="quietHoursStart" className="text-left">
                                {t('sms.settings.startTime')}
                            </Label>
                        </SimpleTooltip>
                        <Input
                            id="quietHoursStart"
                            type="time"
                            value={formData.quietHoursStart || '22:00'}
                            onChange={(e) => handleFieldChange('quietHoursStart', e.target.value)}
                            disabled={!formData.quietHoursEnabled}
                        />
                        <SimpleTooltip content={t('sms.settings.endTime')} position="top">
                            <Label htmlFor="quietHoursEnd" className="text-left">
                                {t('sms.settings.endTime')}
                            </Label>
                        </SimpleTooltip>
                        <Input
                            id="quietHoursEnd"
                            type="time"
                            value={formData.quietHoursEnd || '08:00'}
                            onChange={(e) => handleFieldChange('quietHoursEnd', e.target.value)}
                            disabled={!formData.quietHoursEnabled}
                        />

                        <SimpleTooltip content={t('sms.settings.timezone')} position="top">
                            <Label htmlFor="quietHoursTimezone" className="text-left">
                                {t('sms.settings.timezone')}
                            </Label>
                        </SimpleTooltip>
                        <TimezoneSelect
                            value={formData.quietHoursTimezone || 'America/New_York'}
                            onChange={(value) => handleFieldChange('quietHoursTimezone', value)}
                            disabled={!formData.quietHoursEnabled}
                        />
                        <div /><div />
                    </div>
                </FormBox>
            </div>

            <FixedActionBar
                onSaveWithAction={handleSaveWithAction}
                onCancel={handleCancel}
                isSaving={isSaving}
                cancelText={t('common.cancel')}
                useSaveDropdown={true}
                saveDropdownStorageKey="smsSettings:saveAction"
                saveDisabled={isSaving}
                availableSaveActions={['save']}
            />
        </div>
    );
}
